import { CategoryCard, Skeleton } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import { Heading, HeadingVariant } from '../heading';
import type { CategoriesListProps } from './types';

/**
 * CategoriesList displays a list of category cards.
 * Each card is interactive and can trigger a callback when pressed.
 *
 * @example
 * <CategoriesList
 *   categories={[
 *     {
 *       id: '1',
 *       title: 'Food & Grocery',
 *       images: [
 *         { src: '/image1.jpg', alt: 'Product 1' },
 *         { src: '/image2.jpg', alt: 'Product 2' },
 *         { src: '/image3.jpg', alt: 'Product 3' },
 *       ],
 *     },
 *     // ... more categories
 *   ]}
 *   onCategoryPress={(categoryId) => console.log('Selected:', categoryId)}
 * />
 */
function CategoriesList(props: CategoriesListProps) {
  const { categories, onCategoryPress, className, isLoading = false } = props;

  return (
    <div className="flex flex-col gap-8 bg-white px-30 py-12">
      <div className="flex flex-col gap-16">
        <Heading variant={HeadingVariant.H5}>Browse by product category</Heading>
        <ul
          className={cx('flex flex-wrap gap-5 max-lg:justify-center', className)}
          aria-label="Product categories"
          aria-busy={isLoading}
        >
          {isLoading
            ? Array.from({ length: 8 }).map((_, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: safe to use index as key for loading skeletons
                <li key={`skeleton-${index}`}>
                  <Skeleton className="h-[288px] w-[293px] rounded-none" />
                </li>
              ))
            : categories.map((category) => (
                <li key={category.id}>
                  <CategoryCard
                    title={category.title}
                    images={category.images}
                    onPress={() => onCategoryPress?.(category.id)}
                  />
                </li>
              ))}
        </ul>
      </div>
    </div>
  );
}

export { CategoriesList };
